import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { interval, Subscription } from 'rxjs';
import { APP_VERSION } from '../template/shared/shared.help';

const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_COUNT_KEY = 'versionReloadCount';
const POLL_INTERVAL_MS = 15000; // 15 seconds
const APP_VERSION_KEY = 'APP_VERSION';
const VERSION_HASH_KEY = 'VERSION_HASH';

@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerUpdateService {
  private checkInterval?: Subscription;
  private reloadAttempts = 0;
  private currentVersionHash: string | null = null;

  constructor(private swUpdate: SwUpdate) {
    // Delay initialization slightly to ensure service worker registration has time to complete
    setTimeout(() => {
      this.initializeServiceWorker();
    }, 100);
  }

  private initializeServiceWorker(): void {
    // Initialize APP_VERSION from shared.help.ts if not set
    this.initializeAppVersion();

    if (!this.swUpdate.isEnabled) {
      console.warn('Service Worker is not enabled. Possible reasons:');
      console.warn('1. Service worker files (ngsw-worker.js, ngsw.json) are not present in the build output');
      console.warn('2. HTTPS with self-signed certificate - browsers block service workers in this case');
      console.warn('3. The app must be served over HTTPS (with valid cert) or localhost');
      console.warn('4. Service worker registration failed - check browser console for errors');
      console.warn('Solutions:');
      console.warn('- Use HTTP for local development (http://localhost:4300)');
      console.warn('- Accept the self-signed certificate in browser settings');
      console.warn('- Use localhost instead of IP address');
      return;
    }

    // Initialize VERSION_HASH if we can get current version
    this.initializeVersionHash();

    // Check for updates immediately
    this.checkForUpdates();

    // Set up periodic checking (every 15 seconds)
    this.checkInterval = interval(POLL_INTERVAL_MS).subscribe(() => {
      this.checkForUpdates();
    });

    // Listen for all version update events to track current version
    this.swUpdate.versionUpdates.subscribe((evt) => {
      if (evt.type === 'VERSION_READY') {
        console.log('Version ready event received');
        // Store current version hash for tracking
        this.currentVersionHash = evt.currentVersion.hash;
        this.updateVersionInfo(evt.currentVersion.hash);
        // Activate the update and then handle reload
        this.activateUpdate(evt);
      } else if (evt.type === 'VERSION_DETECTED') {
        console.log('Version detected:', evt.version.hash);
        // Store current version when detected
        this.currentVersionHash = evt.version.hash;
        this.updateVersionInfo(evt.version.hash);
      } else if (evt.type === 'NO_NEW_VERSION_DETECTED') {
        console.log('No new version detected');
        // Still update version info to ensure APP_VERSION is current
        if (this.currentVersionHash) {
          this.updateVersionInfo(this.currentVersionHash);
        }
      }
    });

    // Listen for unrecoverable errors
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('Service Worker unrecoverable error:', event);
      this.reloadAttempts = 0;
      this.resetReloadCount();
    });
  }

  private checkForUpdates(): void {
    this.swUpdate.checkForUpdate()
      .then((hasUpdate) => {
        if (hasUpdate) {
          console.log('Update available, waiting for version ready event...');
        } else {
          console.log('No update available');
          // Reset reload count when versions match
          this.resetReloadCount();
        }
      })
      .catch((err) => {
        console.error('Error checking for updates:', err);
      });
  }

  private initializeAppVersion(): void {
    // Set APP_VERSION from shared.help.ts if not already set or if it's different
    const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);
    if (storedAppVersion !== APP_VERSION) {
      console.log('Setting localStorage APP_VERSION to', APP_VERSION);
      localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
    }
  }

  private initializeVersionHash(): void {
    // Try to get current version hash from service worker if available
    // This will be set when version events fire, but we can also check here
    // Note: SwUpdate doesn't expose currentVersion directly, so we'll rely on events
    // But we can check if there's a stored hash and validate it
    const storedVersionHash = localStorage.getItem(VERSION_HASH_KEY);
    if (storedVersionHash) {
      this.currentVersionHash = storedVersionHash;
      console.log('Initialized VERSION_HASH from localStorage:', storedVersionHash);
    }
  }

  private updateVersionInfo(versionHash: string): void {
    // Update both APP_VERSION and VERSION_HASH
    const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);
    const storedVersionHash = localStorage.getItem(VERSION_HASH_KEY);

    // Update APP_VERSION if it's different (from shared.help.ts)
    if (storedAppVersion !== APP_VERSION) {
      console.log('Updating localStorage APP_VERSION to', APP_VERSION);
      localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
    }

    // Update VERSION_HASH if it's different (from service worker)
    if (storedVersionHash !== versionHash) {
      console.log('Updating localStorage VERSION_HASH to', versionHash);
      localStorage.setItem(VERSION_HASH_KEY, versionHash);
      this.currentVersionHash = versionHash;
    }
  }

  private activateUpdate(event: VersionReadyEvent): void {
    const reloadCount = this.getReloadCount();

    // Check if we've exceeded max reload attempts
    if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
      console.error('Application build process might be broken, current index.html doesn\'t match with service worker manifest, please check build process errors.');
      return;
    }

    console.log('New version available:', event.latestVersion.hash);
    console.log('Current version:', event.currentVersion.hash);

    // Activate the update
    this.swUpdate.activateUpdate()
      .then(() => {
        console.log('Update activated successfully');
        // Increment reload count
        this.incrementReloadCount();
        this.reloadAttempts = reloadCount + 1;

        // Reload the page after a short delay (similar to original 3 second delay)
        setTimeout(() => {
          this.reloadApp();
        }, 3000);
      })
      .catch((err) => {
        console.error('Error activating update:', err);
        // Even if activation fails, try to reload to get the new version
        this.incrementReloadCount();
        setTimeout(() => {
          this.reloadApp();
        }, 3000);
      });
  }

  private handleVersionReady(event: VersionReadyEvent): void {
    // This method is kept for backwards compatibility but activateUpdate is now used
    this.activateUpdate(event);
  }

  private reloadApp(): void {
    console.log('Reloading the page to receive newer version of the app');
    // Note: The new version will be set after reload when the service initializes
    // This matches the original behavior where APP_VERSION was updated after reload
    
    // Reload the page
    window.location.reload();
  }

  private getReloadCount(): number {
    try {
      const count = sessionStorage.getItem(RELOAD_COUNT_KEY);
      return count ? parseInt(count, 10) : 0;
    } catch (e) {
      console.error('Error getting reload count:', e);
      return 0;
    }
  }

  private incrementReloadCount(): void {
    try {
      const count = this.getReloadCount() + 1;
      sessionStorage.setItem(RELOAD_COUNT_KEY, count.toString());
      this.reloadAttempts = count;
    } catch (e) {
      console.error('Error incrementing reload count:', e);
    }
  }

  private resetReloadCount(): void {
    try {
      sessionStorage.removeItem(RELOAD_COUNT_KEY);
      this.reloadAttempts = 0;
    } catch (e) {
      console.error('Error resetting reload count:', e);
    }
  }

  public destroy(): void {
    if (this.checkInterval) {
      this.checkInterval.unsubscribe();
    }
  }
}

